import {
  Alert,
  Autocomplete,
  Avatar,
  colors,
  Grid,
  Icon,
  IconButton,
  Typography,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { IAtleta } from "interfaces/atleta";
import { initialValuesEditAtleta } from "../schemas/initialValues";
import { validationsEdit } from "../schemas/validations";
import { Form, Formik, FormikErrors } from "formik";
import FormField from "layouts/pages/users/new-user/components/FormField";
import { formFieldAtleta } from "../schemas/fieldAtleta";
import {
  cepApplyMask,
  cpfApplyMask,
  getFileNameFromUrl,
  numbersOnly,
  phoneApplyMask,
} from "helpers/utils";
import { formatDate } from "helpers/formatDate";
import TableInscricaoAtleta from "./TableInscricaoAtleta";
import { ChangeEvent, useEffect, useState } from "react";
import { getCep } from "services/uteis";
import { setMessage, useMaterialUIController } from "context";
import {
  deleteAvatarUser,
  resendVerifyEmail,
  uploadAvatarUser,
} from "services/users";
import ResetarSenha from "./ResetarSenha";
import FullScreenDialog from "components/FullScreenDialog";
import { updateAtleta } from "services/atleta";

interface Props {
  atleta: IAtleta;
  open: boolean;
  handleClose: (value: boolean) => void;
  hasUpdated?: () => void;
}

const {
  nome,
  apelido,
  cpf,
  email,
  rg,
  dataNascimento,
  sexo,
  telefone,
  celPhone,
  nomeEmergencia,
  celPhoneEmergencia,
  parentesco,
  cep,
  endereco,
  numero,
  bairro,
  cidade,
  uf,
  complemento,
  equipe,
  planoSaude,
} = formFieldAtleta;

export default function FormAtleta({
  atleta,
  open,
  handleClose,
  hasUpdated,
}: Props) {
  const [, dispatch] = useMaterialUIController();
  const [isLoading, setIsLoading] = useState(false);
  const [showInscricao, setShowInscricao] = useState(false);
  const [showAlertEmailUpdate, setShowAlertEmailUpdate] = useState(false);
  const [previewAvatar, setPreviewAvatar] = useState("");
  const [fileAvatar, setFileAvatar] = useState<globalThis.File | null>(null);

  useEffect(() => {
    setShowInscricao(false);
    setPreviewAvatar(atleta.user.avatar);
  }, [atleta]);

  // Buscar cep
  const handleGetCep = async (
    cep: string,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean
    ) => Promise<void | FormikErrors<{ [key: string]: string }>>
  ) => {
    try {
      setIsLoading(true);

      if (atleta.enderecos[0].cep === cep) return;

      const address = await getCep(cep);

      const fields = address.erro
        ? { endereco: "", bairro: "", cidade: "", uf: "" }
        : {
            endereco: address.logradouro,
            bairro: address.bairro,
            cidade: address.localidade,
            uf: address.uf,
          };

      await Promise.all(
        Object.entries(fields).map(([field, value]) =>
          setFieldValue(field, value)
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Reenviar email de confirmação de autenticidade
  const handleReenviarEmailConfirmacao = async () => {
    await resendVerifyEmail(atleta.user.id).then(({ message }) => {
      setMessage(dispatch, {
        open: true,
        severity: "info",
        text: message,
      });
    });
  };

  // Editar dados do atleta
  const handleEditAtleta = async (values: any) => {
    try {
      await updateAtleta({ id: atleta.id, ...values }).then(
        async ({ data, status }) => {
          handleClose(false);
          hasUpdated && hasUpdated();
          // Verificar se existe arquivo alterando o avatar
          if (fileAvatar) {
            const userId = atleta.user.id;
            const file = new FormData();
            file.append("avatar", fileAvatar);
            await uploadAvatarUser(userId, file);
          }
          setMessage(dispatch, {
            open: true,
            severity: status === 200 ? "success" : "error",
            text:
              status === 200
                ? "Atualizado os dados do Atleta"
                : "Erro ao atualizar os dados do Atleta",
          });
        }
      );
    } catch (error) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: "Erro ao atualizar os dados do Atleta",
      });
    }
  };

  const isUpateEmail = (email: string) => {
    atleta.email !== email
      ? setShowAlertEmailUpdate(true)
      : setShowAlertEmailUpdate(false);
  };

  const handleUploadAvatar = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.currentTarget;
    if (files) {
      const fileSelected = files[0];
      if (fileSelected) {
        setPreviewAvatar(URL.createObjectURL(fileSelected));
        setFileAvatar(fileSelected);
      }
    }
  };

  const handlePhoneChange = (
    field: string,
    event: ChangeEvent<HTMLInputElement>,
    setFieldValue: (
      field: string,
      value: string,
      shouldValidate?: boolean
    ) => void
  ) => {
    const digits = numbersOnly(event.target.value).slice(0, 11);
    setFieldValue(field, digits);
  };

  const phoneDisplayValue = (value: string) =>
    value ? phoneApplyMask(value) : "";

  const handleRemoveAvatar = async () => {
    // Remover avatar localmente
    setPreviewAvatar("");
    setFileAvatar(null);
    // Verificar se existe um avatar salvo, remover do banco de dados
    if (atleta.user.avatar) {
      const userId = atleta.user.id;
      const filename = getFileNameFromUrl(atleta.user.avatar);
      await deleteAvatarUser(userId, filename).then(({ data, status }) => {
        setMessage(dispatch, {
          open: true,
          severity: status === 200 ? "success" : "error",
          text: data.message,
        });
      });
    }
  };

  return (
    <FullScreenDialog
      handleClose={() => handleClose(false)}
      open={open}
      title="Detalhes do atleta"
    >
      <MDBox display="flex" justifyContent="center" my={2}>
        <MDBox id="atleta" width={{ xs: "95%", md: "75%" }}>
          <MDBox
            sx={{
              p: 4,
              my: 4,
              backgroundColor: colors.grey[50],
              borderRadius: "6px",
              border: `1px solid ${colors.grey[400]}`,
            }}
          >
            {/* AVATAR INFORMAÇÕES ATLETA */}
            <MDBox
              display="flex"
              flexDirection={{ xs: "column-reverse", md: "row" }}
              justifyContent="space-between"
              alignItems="start"
              gap={2}
              mb={2}
            >
              <MDBox display="flex" gap={2} alignItems="center">
                <MDBox display="flex" flexDirection="column">
                  <IconButton component="label">
                    <Avatar
                      alt={atleta.nome}
                      src={previewAvatar}
                      sx={{
                        width: 100,
                        height: 100,
                        border: "1px solid #f1f1f1",
                      }}
                    />
                    <input
                      name="avatarUser"
                      accept="image/*"
                      type="file"
                      hidden
                      onChange={handleUploadAvatar}
                    />
                  </IconButton>
                  <MDButton
                    variant="text"
                    color="info"
                    sx={{ mt: -1 }}
                    onClick={handleRemoveAvatar}
                  >
                    Remover
                  </MDButton>
                </MDBox>
                <MDBox>
                  <Typography variant="h5">{atleta.nome}</Typography>
                  <Typography variant="subtitle2">{atleta.apelido}</Typography>
                  <Typography variant="body2">{atleta.email}</Typography>
                </MDBox>
              </MDBox>
              <MDBox
                display="flex"
                flexDirection={{ xs: "row", md: "column" }}
                alignItems="end"
              >
                {atleta.user.emailVerified ? (
                  <MDBox display="flex" gap={1} alignItems="center">
                    <Icon fontSize="large" color="success">
                      verified
                    </Icon>
                    <Typography variant="body2">Email verificado</Typography>
                  </MDBox>
                ) : (
                  <>
                    <MDBox display="flex" gap={1} alignItems="center">
                      <Icon fontSize="large" color="error">
                        gpp_maybe
                      </Icon>
                      <Typography variant="body2">
                        Email não verificado
                      </Typography>
                    </MDBox>

                    <MDButton
                      variant="text"
                      color="info"
                      onClick={handleReenviarEmailConfirmacao}
                    >
                      Reenviar email
                    </MDButton>
                  </>
                )}
                <MDBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  p={1}
                  mt={2}
                  borderRadius="5px"
                  width="60px"
                  sx={{ backgroundColor: "#000000" }}
                >
                  <Typography variant="body1" sx={{ color: colors.grey[50] }}>
                    {atleta.idade}
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.grey[50] }}>
                    anos
                  </Typography>
                </MDBox>
              </MDBox>
            </MDBox>

            <Formik
              initialValues={initialValuesEditAtleta(atleta)}
              validationSchema={validationsEdit}
              onSubmit={(values) => handleEditAtleta(values)}
            >
              {({ values, errors, touched, handleChange, setFieldValue }) => {
                return (
                  <MDBox
                    sx={{
                      p: 2,
                      borderRadius: "5px",
                      border: `1px solid ${colors.grey[400]}`,
                    }}
                  >
                    <Form id="form-atletas">
                      {/* DADOS DO ATLETA */}
                      <MDBox>
                        <MDBox
                          sx={{
                            mb: 4,
                            borderLeft: `4px solid ${colors.orange[400]}`,
                            pl: 1,
                          }}
                        >
                          <Typography variant="h6">Dados do atleta</Typography>
                        </MDBox>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <FormField
                              type={nome.type}
                              label={nome.label}
                              name={nome.name}
                              required
                              variant="outlined"
                              value={values.nome}
                              onChange={handleChange}
                              error={errors.nome && touched.nome}
                              sx={{ background: "white" }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <FormField
                              type={apelido.type}
                              label={apelido.label}
                              name={apelido.name}
                              variant="outlined"
                              value={values.apelido}
                              onChange={handleChange}
                              error={errors.apelido && touched.apelido}
                              sx={{ background: "white" }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <FormField
                              type={cpf.type}
                              label={cpf.label}
                              name={cpf.name}
                              variant="outlined"
                              required
                              value={
                                values.cpf.length === 11
                                  ? cpfApplyMask(values.cpf)
                                  : numbersOnly(values.cpf)
                              }
                              onChange={handleChange}
                              error={errors.cpf && touched.cpf}
                              sx={{ background: "white" }}
                              inputProps={{ maxLength: 14 }}
                            />
                          </Grid>
                        </Grid>

                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={3}>
                            <FormField
                              type={rg.type}
                              label={rg.label}
                              name={rg.name}
                              required
                              variant="outlined"
                              value={values.rg}
                              onChange={handleChange}
                              error={errors.rg && touched.rg}
                              sx={{ background: "white" }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <FormField
                              type={dataNascimento.type}
                              label={dataNascimento.label}
                              name={dataNascimento.name}
                              required
                              variant="outlined"
                              value={values.dataNascimento}
                              onChange={handleChange}
                              InputLabelProps={{ shrink: true }}
                              error={
                                errors.dataNascimento && touched.dataNascimento
                              }
                              sx={{ background: "white" }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Autocomplete
                              options={["Masculino", "Feminino"]}
                              value={values.sexo}
                              onChange={(event, newValue) => {
                                handleChange({
                                  target: {
                                    name: sexo.name,
                                    value: newValue,
                                  },
                                });
                              }}
                              renderInput={(params) => (
                                <FormField
                                  {...params}
                                  variant="outlined"
                                  name={sexo.name}
                                  required
                                  label={sexo.label}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{
                                    background: "white",
                                    width: "100%",
                                    height: "43px", // Ajustar a altura aqui para todos os componentes
                                    "& .MuiOutlinedInput-root": {
                                      padding: "10px", // Remove o padding interno para uniformizar
                                      height: "43px", // Ajusta a altura interna do campo
                                    },
                                  }}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <FormField
                              type={equipe.type}
                              label={equipe.label}
                              name={equipe.name}
                              variant="outlined"
                              value={values.equipe}
                              onChange={handleChange}
                              error={errors.equipe && touched.equipe}
                              sx={{ background: "white" }}
                            />
                          </Grid>
                        </Grid>
                      </MDBox>

                      {/* DADOS DO ATLETA */}
                      <MDBox>
                        <MDBox
                          sx={{
                            my: 2,
                            borderLeft: `4px solid ${colors.orange[400]}`,
                            pl: 1,
                          }}
                        >
                          <Typography variant="h6">Contatos</Typography>
                        </MDBox>
                        {showAlertEmailUpdate && (
                          <Alert
                            severity="warning"
                            color="warning"
                            sx={{ mb: 2 }}
                          >
                            Alteração do email exige que o atleta confirme
                            novamente a autenticidade do email
                          </Alert>
                        )}
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <FormField
                              type={email.type}
                              label={email.label}
                              name={email.name}
                              required
                              variant="outlined"
                              value={values.email}
                              onChange={handleChange}
                              onBlur={() => isUpateEmail(values.email)}
                              error={errors.email && touched.email}
                              sx={{ background: "white" }}
                              InputProps={
                                showAlertEmailUpdate && {
                                  endAdornment: (
                                    <Icon color="error" fontSize="medium">
                                      gpp_maybe
                                    </Icon>
                                  ),
                                }
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <FormField
                              type={celPhone.type}
                              label={celPhone.label}
                              name={celPhone.name}
                              required
                              variant="outlined"
                              value={phoneDisplayValue(values.celPhone)}
                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                handlePhoneChange(
                                  celPhone.name,
                                  e,
                                  setFieldValue
                                )
                              }
                              error={errors.celPhone && touched.celPhone}
                              helperText={
                                errors.celPhone && touched.celPhone
                                  ? String(errors.celPhone)
                                  : undefined
                              }
                              sx={{ background: "white" }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <FormField
                              type={telefone.type}
                              label={telefone.label}
                              name={telefone.name}
                              variant="outlined"
                              value={phoneDisplayValue(values.telefone)}
                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                handlePhoneChange(
                                  telefone.name,
                                  e,
                                  setFieldValue
                                )
                              }
                              error={errors.telefone && touched.telefone}
                              helperText={
                                errors.telefone && touched.telefone
                                  ? String(errors.telefone)
                                  : undefined
                              }
                              sx={{ background: "white" }}
                            />
                          </Grid>
                        </Grid>

                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={3}>
                            <FormField
                              type={nomeEmergencia.type}
                              label={nomeEmergencia.label}
                              name={nomeEmergencia.name}
                              variant="outlined"
                              value={values.nomeEmergencia}
                              onChange={handleChange}
                              error={
                                errors.nomeEmergencia && touched.nomeEmergencia
                              }
                              sx={{ background: "white" }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <FormField
                              type={celPhoneEmergencia.type}
                              label={celPhoneEmergencia.label}
                              name={celPhoneEmergencia.name}
                              variant="outlined"
                              value={phoneDisplayValue(values.celPhoneEmergencia)}
                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                handlePhoneChange(
                                  celPhoneEmergencia.name,
                                  e,
                                  setFieldValue
                                )
                              }
                              error={
                                errors.celPhoneEmergencia &&
                                touched.celPhoneEmergencia
                              }
                              helperText={
                                errors.celPhoneEmergencia &&
                                touched.celPhoneEmergencia
                                  ? String(errors.celPhoneEmergencia)
                                  : undefined
                              }
                              sx={{ background: "white" }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <FormField
                              type={parentesco.type}
                              label={parentesco.label}
                              name={parentesco.name}
                              variant="outlined"
                              value={values.parentesco}
                              onChange={handleChange}
                              error={errors.parentesco && touched.parentesco}
                              sx={{ background: "white" }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <FormField
                              type={planoSaude.type}
                              label={planoSaude.label}
                              name={planoSaude.name}
                              variant="outlined"
                              value={values.planoSaude}
                              onChange={handleChange}
                              error={errors.planoSaude && touched.planoSaude}
                              sx={{ background: "white" }}
                            />
                          </Grid>
                        </Grid>
                      </MDBox>

                      {/* ENDEREÇO DO ATLETA */}
                      <MDBox>
                        <MDBox
                          sx={{
                            my: 2,
                            borderLeft: `4px solid ${colors.orange[400]}`,
                            pl: 1,
                          }}
                        >
                          <Typography variant="h6">Endereço</Typography>
                        </MDBox>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={3}>
                            <FormField
                              type={cep.type}
                              label={cep.label}
                              name={cep.name}
                              required
                              variant="outlined"
                              value={
                                values.cep.length === 8
                                  ? cepApplyMask(values.cep)
                                  : numbersOnly(values.cep)
                              }
                              onChange={handleChange}
                              onBlur={() =>
                                handleGetCep(values.cep, setFieldValue)
                              }
                              error={errors.cep && touched.cep}
                              sx={{ background: "white" }}
                              inputProps={{ maxLength: 10 }}
                              InputProps={{
                                endAdornment: (
                                  <Icon
                                    fontSize="medium"
                                    sx={{
                                      animation: isLoading
                                        ? "spin 1s linear infinite"
                                        : "",
                                      "@keyframes spin": {
                                        "0%": { transform: "rotate(0deg)" },
                                        "100%": { transform: "rotate(360deg)" },
                                      },
                                    }}
                                  >
                                    rotate_right
                                  </Icon>
                                ),
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={7}>
                            <FormField
                              type={endereco.type}
                              label={endereco.label}
                              name={endereco.name}
                              variant="outlined"
                              required
                              value={values.endereco}
                              onChange={handleChange}
                              error={errors.endereco && touched.endereco}
                              sx={{ background: "white" }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            <FormField
                              type={numero.type}
                              label={numero.label}
                              name={numero.name}
                              variant="outlined"
                              value={values.numero}
                              required
                              onChange={handleChange}
                              error={errors.numero && touched.numero}
                              sx={{ background: "white" }}
                            />
                          </Grid>
                        </Grid>

                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={4}>
                            <FormField
                              type={bairro.type}
                              label={bairro.label}
                              name={bairro.name}
                              variant="outlined"
                              value={values.bairro}
                              onChange={handleChange}
                              error={errors.bairro && touched.nomeEmergencia}
                              sx={{ background: "white" }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <FormField
                              type={cidade.type}
                              label={cidade.label}
                              name={cidade.name}
                              variant="outlined"
                              value={values.cidade}
                              onChange={handleChange}
                              error={
                                errors.cidade && touched.celPhoneEmergencia
                              }
                              sx={{ background: "white" }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={1}>
                            <FormField
                              type={uf.type}
                              label={uf.label}
                              name={uf.name}
                              variant="outlined"
                              value={values.uf}
                              onChange={handleChange}
                              error={errors.uf && touched.uf}
                              sx={{ background: "white" }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <FormField
                              type={complemento.type}
                              label={complemento.label}
                              name={complemento.name}
                              variant="outlined"
                              value={values.complemento}
                              onChange={handleChange}
                              error={errors.complemento && touched.complemento}
                              sx={{ background: "white" }}
                            />
                          </Grid>
                        </Grid>
                      </MDBox>

                      <MDButton type="submit" variant="contained" color="info">
                        Salvar Alterações
                      </MDButton>
                    </Form>
                  </MDBox>
                );
              }}
            </Formik>

            {/* Lista de inscrições realizada pelo atleta */}
            <MDBox
              p={2}
              mt={2}
              borderRadius="6px"
              bgColor="white"
              border={`1px solid ${colors.grey[500]}`}
            >
              <MDBox my={1} alignItems="center">
                {atleta.inscritos.length > 0 ? (
                  <MDBox
                    display="flex"
                    flexDirection={{ xs: "column", md: "row" }}
                    gap={2}
                    alignItems="center"
                    justifyContent={{ md: "space-between", xs: "center" }}
                  >
                    <MDBox
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{
                        borderLeft: `4px solid ${colors.orange[400]}`,
                        pl: 1,
                      }}
                    >
                      <Typography variant="h6">
                        Número de inscrições do atleta:{" "}
                      </Typography>
                      <Typography variant="body2">
                        {atleta.inscritos.length}
                      </Typography>
                    </MDBox>
                    <MDBox>
                      <MDButton
                        onClick={() => setShowInscricao(!showInscricao)}
                        variant="outlined"
                        color="info"
                      >
                        {showInscricao
                          ? "Ocultar Inscrições"
                          : "Exibir inscrições"}
                      </MDButton>
                    </MDBox>
                  </MDBox>
                ) : (
                  <MDBox display="flex" gap={2}>
                    <Typography variant="h6">
                      Atleta não realizou nenhuma inscrição
                    </Typography>
                  </MDBox>
                )}
              </MDBox>

              {atleta.inscritos.length > 0 && showInscricao && (
                <TableInscricaoAtleta inscricoes={atleta.inscritos} />
              )}
            </MDBox>

            {/* Solicitado alteração de senha */}

            <MDBox
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              justifyContent={{ md: "space-between", xs: "center" }}
              alignItems="center"
              gap={2}
              sx={{
                mt: 2,
                border: `1px solid ${colors.grey[500]}`,
                p: 2,
                borderRadius: 2,
                background: "white",
              }}
            >
              <MDBox display="flex" flexDirection="column">
                <MDBox
                  sx={{
                    mb: 1,
                    borderLeft: `4px solid ${colors.orange[400]}`,
                    pl: 1,
                  }}
                >
                  <Typography variant="h6">Alteração de senha</Typography>
                </MDBox>
                {atleta.user.passwordResetToken && (
                  <>
                    <Typography variant="body2">{`Usuário solicitou alteração de senha, enviado código no email: ${atleta.email}`}</Typography>
                    <MDBox display="flex" alignItems="center">
                      <Typography variant="body2" fontWeight="bold">
                        Código:
                      </Typography>
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        sx={{ mx: 1, letterSpacing: 4 }}
                      >
                        {atleta.user.passwordResetToken}
                      </Typography>
                    </MDBox>
                  </>
                )}
              </MDBox>

              <ResetarSenha userId={atleta.user.id} />
            </MDBox>

            {/* Actions */}
            <MDBox
              display="flex"
              flexDirection={{ xs: "column-reverse", sm: "row" }}
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              mt={2}
            >
              <MDBox display="flex" justifyContent="flex-end" mt={2}>
                <Typography variant="caption" fontWeight="medium">
                  Inscrito em:{" "}
                  {formatDate(atleta.createAt, "dd/MM/yyyy HH:mm:ss")}
                </Typography>
              </MDBox>
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>
    </FullScreenDialog>
  );
}
